// PerformanceGoals.jsx - Mobile Responsive Version
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const PerformanceGoals = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Core state
  const [view, setView] = useState('hierarchy');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [hierarchy, setHierarchy] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Refs to prevent duplicate calls
  const fetchingRef = useRef({
    hierarchy: false,
    campaigns: false
  });
  const hasInitialized = useRef(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
  const CAMPAIGNS_PER_PAGE = 50;
  
  // Optimized fetch functions
  const fetchHierarchy = useCallback(async (force = false) => {
    if (fetchingRef.current.hierarchy) return;
    
    if (!force) {
      const cached = sessionStorage.getItem('benchmark_hierarchy');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 300000) {
          setHierarchy(data);
          return;
        }
      }
    }
    
    fetchingRef.current.hierarchy = true;
    setLoading(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await fetch(`${BACKEND_URL}/api/benchmarks/hierarchy`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch hierarchy');
      
      const data = await response.json();
      setHierarchy(data.hierarchy);
      
      sessionStorage.setItem('benchmark_hierarchy', JSON.stringify({
        data: data.hierarchy,
        timestamp: Date.now()
      }));
    } catch (error) {
      logger.error('Error fetching hierarchy:', error);
      showNotification('Failed to load benchmarks', 'error');
    } finally {
      setLoading(false);
      fetchingRef.current.hierarchy = false;
    }
  }, [BACKEND_URL]);
  
  const fetchCampaigns = useCallback(async (force = false) => {
    if (fetchingRef.current.campaigns) return;
    
    if (!force && campaigns.length > 0) return;
    
    const cached = sessionStorage.getItem('campaign_list');
    if (!force && cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 300000) {
        setCampaigns(data);
        return;
      }
    }
    
    fetchingRef.current.campaigns = true;
    
    try {
      const { data, error } = await supabase
        .from('campaign_detective_snapshot')
        .select('campaign_id, name, campaign_objective, platform, status, spend, ctr, cpc')
        .eq('user_id', user.id)
        .eq('status', 'ACTIVE') 
        .order('spend', { ascending: false })
        .limit(200);
      
      if (error) throw error;
      
      setCampaigns(data || []);
      
      sessionStorage.setItem('campaign_list', JSON.stringify({
        data: data || [],
        timestamp: Date.now()
      }));
    } catch (error) {
      logger.error('Error fetching campaigns:', error);
      showNotification('Failed to load campaigns', 'error');
    } finally {
      fetchingRef.current.campaigns = false;
    }
  }, [user, campaigns.length]);
  
  const fetchCampaignBenchmark = async (campaignId) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/benchmarks/campaign/${campaignId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch campaign benchmark');
      
      const data = await response.json();
      setBenchmarkData(data);
    } catch (error) {
      logger.error('Error fetching campaign benchmark:', error);
      showNotification('Failed to load campaign benchmark', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSuggestions = async (campaignId, aggressive = false) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/benchmarks/suggest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ campaignId, aggressive })
      });
      
      if (!response.ok) throw new Error('Failed to get suggestions');
      
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      logger.error('Error getting suggestions:', error);
      showNotification('Failed to get AI suggestions', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const saveCampaignBenchmark = async () => {
    if (!selectedCampaign || !benchmarkData) return;
    
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${BACKEND_URL}/api/benchmarks/campaign/${selectedCampaign.campaign_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            benchmarks: benchmarkData.benchmarks
          })
        }
      );
      
      if (!response.ok) throw new Error('Failed to save');
      
      showNotification('Benchmark saved successfully', 'success');
      sessionStorage.removeItem('benchmark_hierarchy');
      fetchHierarchy(true);
    } catch (error) {
      logger.error('Error saving:', error);
      showNotification('Failed to save benchmark', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const saveBulkBenchmarks = async (applyToObjective = false, applyToPlatform = false) => {
    if (selectedCampaigns.length === 0) return;
    
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/benchmarks/bulk-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaignIds: selectedCampaigns,
          benchmarks: benchmarkData?.benchmarks || {},
          applyToObjective,
          applyToPlatform
        })
      });
      
      if (!response.ok) throw new Error('Failed to bulk update');
      
      const result = await response.json();
      showNotification(`Updated ${result.updatedCount} campaigns`, 'success');
      sessionStorage.removeItem('benchmark_hierarchy');
      fetchHierarchy(true);
      setSelectedCampaigns([]);
    } catch (error) {
      logger.error('Error bulk updating:', error);
      showNotification('Failed to bulk update', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  
  const handleBenchmarkChange = (metric, value) => {
    setBenchmarkData(prev => ({
      ...prev,
      benchmarks: {
        ...prev.benchmarks,
        [metric]: value === '' ? null : parseFloat(value)
      }
    }));
  };
  
  const applySuggestions = () => {
    if (suggestions?.suggestions) {
      setBenchmarkData(prev => ({
        ...prev,
        benchmarks: suggestions.suggestions
      }));
      setShowSuggestions(false);
      showNotification('AI suggestions applied', 'success');
    }
  };
  
  const filteredCampaigns = useMemo(() => {
    if (!searchTerm) return campaigns.slice(0, CAMPAIGNS_PER_PAGE);
    
    return campaigns
      .filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.campaign_objective?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, CAMPAIGNS_PER_PAGE);
  }, [campaigns, searchTerm]);
  
  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchHierarchy();
      fetchCampaigns();
    }
  }, [user, fetchHierarchy, fetchCampaigns]);
  
  useEffect(() => {
    return () => {
      fetchingRef.current = {
        hierarchy: false,
        campaigns: false
      };
      hasInitialized.current = false;
    };
  }, []);
  
  const getCurrencySymbol = (currency = 'USD') => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', NGN: '₦' };
    return symbols[currency] || '$';
  };
  
  const getStatusColor = (status) => {
    const colors = {
      EXCEEDING: 'text-green-400',
      ACHIEVING: 'text-green-400',
      MEETING: 'text-yellow-400',
      APPROACHING: 'text-yellow-400',
      MISSING: 'text-red-400',
      BELOW: 'text-red-400'
    };
    return colors[status] || 'text-gray-400';
  };
  
  const renderHierarchyView = () => (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-gray-800/50 rounded-lg p-4 md:p-6 border border-gray-700">
        <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Benchmark Hierarchy</h3>
        <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-6">
          Your benchmarks follow a hierarchy: Campaign-specific → Platform defaults → System defaults
        </p>
        
        {hierarchy && (
          <div className="space-y-3 md:space-y-4">
            {Object.keys(hierarchy.campaigns).length > 0 && (
              <div>
                <h4 className="text-sm md:text-base text-green-400 font-semibold mb-2">
                  Campaign Overrides ({Object.keys(hierarchy.campaigns).length})
                </h4>
                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(hierarchy.campaigns).slice(0, 5).map(([id, data]) => (
                    <div key={id} className="bg-gray-900 p-3 rounded">
                      <p className="text-sm text-gray-200 truncate">{data.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.keys(data.benchmarks).length} custom metrics
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {Object.keys(hierarchy.platform).length > 0 && (
              <div>
                <h4 className="text-sm md:text-base text-blue-400 font-semibold mb-2">
                  Platform Defaults
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(hierarchy.platform).map(([platform, categories]) => (
                    <div key={platform} className="bg-gray-900 p-3 rounded">
                      <p className="text-sm text-gray-200 capitalize">{platform}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.keys(categories).length} objectives configured
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="bg-gray-900 p-3 md:p-4 rounded">
              <h4 className="text-sm md:text-base text-gray-400 font-semibold mb-1 md:mb-2">System Defaults</h4>
              <p className="text-xs text-gray-500">
                Fallback values for unconfigured campaigns. Always available.
              </p>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <button
          onClick={() => setView('campaign')}
          className="w-full sm:w-auto bg-green-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-green-500 transition-all"
        >
          Configure Campaign
        </button>
        <button
          onClick={() => setView('bulk')}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-blue-500 transition-all"
        >
          Bulk Edit
        </button>
      </div>
    </div>
  );
  
  const renderCampaignView = () => (
    <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6">
      {/* Campaign selector */}
      <div className="md:col-span-1">
        <h3 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-4">Select Campaign</h3>
        
        <input
          type="text"
          placeholder="Search campaigns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full mb-3 md:mb-4 bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm md:text-base text-white"
        />
        
        <div className="space-y-2 max-h-[400px] md:max-h-[600px] overflow-y-auto">
          {filteredCampaigns.map(campaign => (
            <button
              key={campaign.campaign_id}
              onClick={() => {
                setSelectedCampaign(campaign);
                fetchCampaignBenchmark(campaign.campaign_id);
              }}
              className={`w-full text-left p-3 md:p-4 rounded-lg border transition-all ${
                selectedCampaign?.campaign_id === campaign.campaign_id
                  ? 'bg-green-600/20 border-green-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
              }`}
            >
              <p className="font-medium text-sm text-white truncate">{campaign.name}</p>
              <p className="text-xs text-gray-400 mt-1 truncate">{campaign.campaign_objective}</p>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>CTR: {campaign.ctr}%</span>
                <span>Spend: ${campaign.spend}</span>
              </div>
            </button>
          ))}
        </div>
        
        {campaigns.length > CAMPAIGNS_PER_PAGE && (
          <p className="text-xs text-gray-500 mt-2">
            Showing {filteredCampaigns.length} of {campaigns.length} campaigns
          </p>
        )}
      </div>
      
      {/* Benchmark editor */}
      <div className="md:col-span-2">
        {!selectedCampaign ? (
          <div className="flex items-center justify-center h-48 md:h-full bg-gray-800/30 rounded-lg border border-gray-700">
            <p className="text-sm md:text-base text-gray-400 px-4 text-center">Select a campaign to configure benchmarks</p>
          </div>
        ) : benchmarkData ? (
          <div className="bg-gray-800/50 p-4 md:p-6 rounded-lg border border-gray-700">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 md:mb-6 gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-white truncate">{selectedCampaign.name}</h3>
                <p className="text-xs md:text-sm text-gray-400 mt-1 truncate">
                  {selectedCampaign.campaign_objective} • {benchmarkData.benchmarkSource} benchmarks
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => fetchSuggestions(selectedCampaign.campaign_id, false)}
                  className="text-xs md:text-sm bg-blue-600/20 text-blue-400 px-3 md:px-4 py-2 rounded hover:bg-blue-600/30 transition-all whitespace-nowrap"
                >
                  AI Suggest
                </button>
                <button
                  onClick={() => fetchCampaignBenchmark(selectedCampaign.campaign_id)}
                  className="text-xs md:text-sm text-gray-400 hover:text-gray-300 px-2"
                >
                  Reset
                </button>
              </div>
            </div>
            
            {showSuggestions && suggestions && (
              <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                <h4 className="text-sm md:text-base text-blue-400 font-semibold mb-2">AI Suggestions</h4>
                <p className="text-xs md:text-sm text-gray-300 mb-3">{suggestions.reasoning}</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3">
                  {suggestions.insights?.map((insight, i) => (
                    <span key={i} className="text-xs bg-gray-700 px-2 py-1 rounded">
                      {insight}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={applySuggestions}
                    className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded text-xs md:text-sm hover:bg-blue-500"
                  >
                    Apply Suggestions
                  </button>
                  <button
                    onClick={() => setShowSuggestions(false)}
                    className="w-full sm:w-auto text-gray-400 px-4 py-2 text-xs md:text-sm hover:text-gray-300"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            
            <div className="space-y-3 md:space-y-4">
              {benchmarkData.relevantMetrics && Object.entries(benchmarkData.relevantMetrics).map(([key, def]) => (
                <div key={key}>
                  <label className="block text-xs md:text-sm text-gray-400 mb-1">
                    {def.goal === 'min' ? 'Min ' : 'Max '}{def.label}
                    {def.unit === 'currency' && ` (${getCurrencySymbol(benchmarkData.currency)})`}
                    {def.unit === '%' && ' (%)'}
                    {def.unit === 'x' && ' (x)'}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="number"
                      step={def.step}
                      value={benchmarkData.benchmarks[key] ?? ''}
                      onChange={(e) => handleBenchmarkChange(key, e.target.value)}
                      className="flex-1 bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-sm md:text-base text-white focus:border-green-500"
                      placeholder={`Current: ${benchmarkData.campaign.currentMetrics[key] || 'N/A'}`}
                    />
                    {benchmarkData.campaign.currentMetrics[key] !== undefined && (
                      <div className="flex items-center px-3 py-2 sm:py-0 bg-gray-900 sm:bg-transparent rounded-md sm:rounded-none">
                        <span className="text-xs text-gray-500">
                          Current: {benchmarkData.campaign.currentMetrics[key]}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3 md:gap-4 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-700">
              {notification && (
                <p className={`text-xs md:text-sm text-center sm:text-left ${
                  notification.type === 'error' ? 'text-red-400' : 'text-green-400'
                }`}>
                  {notification.message}
                </p>
              )}
              <button
                onClick={saveCampaignBenchmark}
                disabled={saving}
                className="w-full sm:w-auto bg-green-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-bold hover:bg-green-500 disabled:opacity-50 transition-all"
              >
                {saving ? 'Saving...' : 'Save Benchmark'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 md:h-full">
            <div className="text-sm md:text-base text-gray-400">Loading benchmark data...</div>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderBulkView = () => (
  <div className="space-y-4 md:space-y-6">
    <div className="bg-gray-800/50 rounded-lg p-4 md:p-6 border border-gray-700">
      <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Bulk Edit Benchmarks</h3>
      <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-6">
        Select multiple campaigns to apply the same benchmarks
      </p>
      
      <input
        type="text"
        placeholder="Search campaigns..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full mb-3 md:mb-4 bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm md:text-base text-white"
      />
      
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 md:mb-4 gap-3">
          <h4 className="text-sm md:text-base text-white font-semibold">Select Campaigns</h4>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCampaigns(filteredCampaigns.map(c => c.campaign_id))}
              className="text-xs md:text-sm text-blue-400 hover:text-blue-300 px-2 py-1"
            >
              Select Visible
            </button>
            <button
              onClick={() => setSelectedCampaigns([])}
              className="text-xs md:text-sm text-gray-400 hover:text-gray-300 px-2 py-1"
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-2 max-h-[400px] md:max-h-[500px] overflow-y-auto">
          {filteredCampaigns.map(campaign => (
            <label
              key={campaign.campaign_id}
              className="flex items-start sm:items-center p-3 bg-gray-900 rounded hover:bg-gray-800 cursor-pointer gap-3"
            >
              <input
                type="checkbox"
                checked={selectedCampaigns.includes(campaign.campaign_id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedCampaigns([...selectedCampaigns, campaign.campaign_id]);
                  } else {
                    setSelectedCampaigns(selectedCampaigns.filter(id => id !== campaign.campaign_id));
                  }
                }}
                className="mt-0.5 sm:mt-0 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{campaign.name}</p>
                <p className="text-xs text-gray-500 truncate">{campaign.campaign_objective}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      {selectedCampaigns.length > 0 && (
        <div className="border-t border-gray-700 pt-4 md:pt-6">
          <p className="text-sm text-gray-400 mb-3 md:mb-4">
            {selectedCampaigns.length} campaign{selectedCampaigns.length !== 1 ? 's' : ''} selected
          </p>
          
          {/* Benchmark Input Section */}
          <div className="mb-4 bg-gray-900/50 p-4 rounded-lg border border-gray-700">
            <h4 className="text-sm font-semibold text-white mb-3">Set Benchmark Values</h4>
            <p className="text-xs text-gray-400 mb-4">Enter the benchmark values you want to apply to all selected campaigns</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Min CTR (%)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 2.0"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('ctr', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Max CPC ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 1.50"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('cpc', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Max CPM ($)</label>
                <input
                  type="number"
                  step="0.10"
                  placeholder="e.g., 10.00"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('cpm', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Min Conversion Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 3.0"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('conversion_rate', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Max Cost Per Lead ($)</label>
                <input
                  type="number"
                  step="0.50"
                  placeholder="e.g., 25.00"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('cost_per_lead', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Min ROAS (x)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 2.5"
                  onChange={(e) => {
                    if (!benchmarkData) setBenchmarkData({ benchmarks: {} });
                    handleBenchmarkChange('roas', e.target.value);
                  }}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:border-green-500"
                />
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <button
              onClick={() => saveBulkBenchmarks(false, false)}
              disabled={saving || !benchmarkData?.benchmarks || Object.keys(benchmarkData.benchmarks).length === 0}
              className="w-full sm:w-auto bg-green-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Applying...' : 'Apply to Selected'}
            </button>
            <button
              onClick={() => saveBulkBenchmarks(true, false)}
              disabled={saving || !benchmarkData?.benchmarks || Object.keys(benchmarkData.benchmarks).length === 0}
              className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Applying...' : 'Apply to Similar Objectives'}
            </button>
          </div>
          
          {notification && (
            <p className={`text-xs md:text-sm text-center mt-3 ${
              notification.type === 'error' ? 'text-red-400' : 'text-green-400'
            }`}>
              {notification.message}
            </p>
          )}
        </div>
      )}
    </div>
  </div>
);
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate('/campaign-detective')}
            className="bg-gray-700/50 text-gray-300 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-gray-600/50 transition-all flex items-center gap-2"
          >
            ← Back to Campaign Detective
          </button>
        </div>
        
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-black bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text mb-2">
          Performance Goals
        </h1>
        <p className="text-sm md:text-base text-gray-400 mb-6 md:mb-8">
          Set custom benchmarks to align AI analysis with your business objectives
        </p>
        
        {/* View tabs */}
        <div className="flex gap-2 md:gap-4 mb-6 md:mb-8 border-b border-gray-700 overflow-x-auto">
          <button
            onClick={() => setView('hierarchy')}
            className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold transition-all whitespace-nowrap ${
              view === 'hierarchy'
                ? 'text-green-400 border-b-2 border-green-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setView('campaign')}
            className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold transition-all whitespace-nowrap ${
              view === 'campaign'
                ? 'text-green-400 border-b-2 border-green-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Campaign-Specific
          </button>
          <button
            onClick={() => setView('bulk')}
            className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold transition-all whitespace-nowrap ${
              view === 'bulk'
                ? 'text-green-400 border-b-2 border-green-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Bulk Edit
          </button>
        </div>
        
        {/* Main content */}
        {loading && !hierarchy && !campaigns.length ? (
          <div className="flex items-center justify-center py-12 md:py-20">
            <div className="text-sm md:text-base text-gray-400">Loading...</div>
          </div>
        ) : (
          <>
            {view === 'hierarchy' && renderHierarchyView()}
            {view === 'campaign' && renderCampaignView()}
            {view === 'bulk' && renderBulkView()}
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceGoals;